<script lang="ts">
	import { shoppingCart } from '$lib/state/shoppingCart.svelte';
	import { trackAddToCart, trackEvent } from '$lib/segment/client';
	import { getPromoOffAmount } from '$lib/utils/stripe';
	import { formatMoney, inDollars } from '$lib/utils/money';
	import { fade, slide } from 'svelte/transition';
	import Close from '$lib/icons/close.svelte';
	import Button from '$lib/buttons/v3/button.svelte';
	import Lock from '$lib/icons/lock.svelte';
	import SbRichtext from '$lib/storyblok/components/sbRichtext.svelte';
	import Truck from '$lib/icons/truck.svelte';
	import { isEmpty } from '$lib/utils/storyblok/richtext';
	import Promo from '$lib/icons/promo.svelte';
	import Blok from '$lib/storyblok/blok.svelte';
	import { checkoutSession } from '$lib/state/checkoutSession.svelte';
	import SelectedProduct from '$lib/storyblok/parts/cart/selectedProduct.svelte';
	import MillStone from '$lib/icons/millStone.svelte';

	function close() {
		shoppingCart.close();
		trackEvent('Cart Modal Dismissed');
	}

	// prevent double tracking when the prices get invalidated while the cart is open
	let hasTrackedCart = $state(false);

	$effect.pre(() => {
		if (shoppingCart.isOpen && !hasTrackedCart) {
			if (checkoutSession.data.product) {
				trackAddToCart(
					checkoutSession.data.product.price,
					checkoutSession.data.product.promotionCode
				);
				hasTrackedCart = true;
			}
		} else if (!shoppingCart.isOpen) {
			hasTrackedCart = false;
		}
	});

	let amountOff = $derived(
		checkoutSession.data.product?.price.unit_amount &&
			checkoutSession.data.product.promotionCode?.coupon
			? getPromoOffAmount(
					checkoutSession.data.product.price.unit_amount,
					checkoutSession.data.product.promotionCode.coupon
				)
			: 0
	);

	let shippingValue = $derived(
		shoppingCart.data?.original_shipping_value
			? parseInt(shoppingCart.data.original_shipping_value, 10) * 100
			: 0
	);

	let checkoutCta = $derived.by(() => {
		if (
			checkoutSession.baseProduct &&
			typeof checkoutSession.baseProduct.custom_cart_cta === 'string' &&
			checkoutSession.baseProduct.custom_cart_cta.length > 0
		)
			return checkoutSession.baseProduct.custom_cart_cta;

		if (
			checkoutSession.selectedProduct &&
			checkoutSession.selectedProduct.component === 'product_details' &&
			checkoutSession.selectedProduct.custom_cart_cta &&
			checkoutSession.selectedProduct.custom_cart_cta.length > 0
		)
			return checkoutSession.selectedProduct.custom_cart_cta;

		if (shoppingCart.data?.cta_label && shoppingCart.data?.cta_label.length > 0)
			return shoppingCart.data?.cta_label;

		return 'Secure Checkout';
	});

	let cartFinePrint = $derived(
		checkoutSession.selectedProduct?.component === 'product_details' &&
			checkoutSession.selectedProduct.cart_fine_print &&
			!isEmpty(checkoutSession.selectedProduct.cart_fine_print)
			? checkoutSession.selectedProduct.cart_fine_print
			: shoppingCart.data?.price_subline && !isEmpty(shoppingCart.data.price_subline)
				? shoppingCart.data.price_subline
				: undefined
	);
</script>

{#snippet promoCode(code: string)}
	<div in:slide class="flex items-center justify-between py-1">
		<p
			class="flex items-center gap-x-1 rounded-[4px] bg-green-6 p-1 text-[12px]/[100%] tracking-[-.12px] text-green-1 lg:rounded-lg lg:p-2"
		>
			<Promo class="h-4 w-4" />
			{code}
		</p>
		<span class="text-green-3">
			-{formatMoney(
				inDollars(Math.min(amountOff, checkoutSession.data.product?.price.unit_amount || amountOff))
			)}
		</span>
	</div>
{/snippet}

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<section
	onclick={(e) => {
		e.stopPropagation();
	}}
	class="scrollbar-hide relative flex h-full cursor-auto flex-col justify-between overflow-y-scroll bg-white px-5 pt-5 text-brown-1 lg:rounded-3xl lg:px-8 lg:pt-6"
>
	{#if !checkoutSession.updatingBundle && !shoppingCart.isUpdating}
		<div transition:fade>
			<div class="flex items-center justify-between">
				<h1 class="text-[24px]/[108%] lg:text-[32px]/[36px]">Cart</h1>
				<Button title="Close" class="btn-brown" onclick={close}>
					<Close />
				</Button>
			</div>
			<SelectedProduct />
		</div>
	{/if}

	<div class="my-6 h-full">
		{#if checkoutSession.updatingBundle || shoppingCart.isUpdating}
			<div class="flex h-full min-h-52 items-center justify-center">
				<MillStone
					class="h-16 w-16 animate-[spin_3s_linear_infinite] text-brown-2 lg:h-20 lg:w-20"
				/>
			</div>
		{:else}
			{#if shoppingCart.data?.body && shoppingCart.data.body.length > 0}
				<div
					class={[
						'flex flex-col gap-y-4',
						{ 'mb-4': shoppingCart.products && shoppingCart.products.length > 1 }
					]}
				>
					{#each shoppingCart.data.body as blok}
						<Blok content={blok} />
					{/each}
				</div>
			{/if}
			{#if shoppingCart.components && shoppingCart.products && shoppingCart.products.length > 1}
				<div class="flex flex-col gap-y-3">
					{#each shoppingCart.components as product}
						{#if product.component !== 'product_details' || shoppingCart.showProductSelectors}
							<Blok content={product} />
						{/if}
					{/each}
				</div>
			{/if}
		{/if}
	</div>

	{#if !checkoutSession.updatingBundle && !shoppingCart.isUpdating}
		<div class="mt-4 text-[14px]/[160%] lg:text-[16px]" transition:fade>
			{#if shoppingCart.data?.display_shipping}
				<div class="flex justify-between py-1">
					<div class="flex items-center gap-x-2">
						<p>Shipping</p>

						{#if checkoutSession.isFreeShipping && shippingValue > 0}
							<div in:slide class="flex items-center justify-between py-1">
								<p
									class="flex items-center gap-x-1 rounded-[4px] bg-orange-peach p-1 text-[12px]/[100%] tracking-[-.12px] text-brown-1 lg:rounded-lg"
								>
									<Promo class="h-4 w-4" />
									Promo applied
								</p>
							</div>
						{/if}
					</div>

					<p class="flex items-center gap-x-1 text-brown-1/60">
						{#if checkoutSession.isFreeShipping && shippingValue > 0}
							<span class="line-through">{formatMoney(inDollars(shippingValue))} </span>
							<span class="text-green-3"> Free</span>
						{:else}
							{formatMoney(inDollars(checkoutSession.shippingCosts))}
						{/if}
					</p>
				</div>
			{/if}

			{#if shoppingCart.data?.display_taxes}
				<div class="flex justify-between py-1">
					<p>Taxes</p>
					<p class="text-brown-1/60">Calculated at checkout</p>
				</div>
			{/if}

			{#if shoppingCart.data?.display_discount && checkoutSession.data.product?.promotionCode?.active}
				{@render promoCode(checkoutSession.data.product.promotionCode.code)}
			{/if}

			<div
				class="mt-3 flex justify-between border-t border-brown-1/10 py-4 text-[16px]/[110%] font-semibold text-brown-1 lg:text-[20px]"
			>
				<p>Estimated total</p>

				{#if checkoutSession.data.product && checkoutSession.data.product.price.unit_amount !== null}
					<p>
						{formatMoney(
							inDollars(
								Math.max(
									checkoutSession.data.product.price.unit_amount -
										amountOff +
										checkoutSession.shippingCosts,
									0
								)
							),
							{
								forceFractionDigits: true
							}
						)}
					</p>
				{/if}
			</div>
			{#if cartFinePrint}
				<div class="lg:text-[14px empty:hiddenr text-[12px]/[160%] text-brown-4">
					<SbRichtext text={cartFinePrint} />
				</div>
			{/if}
		</div>

		<div class="floating-background sticky bottom-0 pb-4 pt-5">
			<Button
				data-testid="shopping-cart-proceed-btn"
				disabled={checkoutSession.processing}
				onclick={async () => {
					await checkoutSession.submit();
				}}
				class="btn-large w-full"
			>
				{#if shoppingCart.data?.display_cta_lock_icon}
					<Lock />
				{/if}
				<span>{checkoutSession.processing ? shoppingCart.data?.processing_label : checkoutCta}</span
				>
			</Button>

			{#if shoppingCart.data?.shipping_text && !isEmpty(shoppingCart.data.shipping_text)}
				<div class="mt-3 flex items-center justify-center gap-x-2 text-[12px]/[160%] text-brown-1">
					<Truck class="h-4 w-4" />
					<SbRichtext text={shoppingCart.data.shipping_text} />
				</div>
			{/if}

			{#if shoppingCart.data?.below_cta_content && shoppingCart.data.below_cta_content.length > 0}
				<div class="mt-4 empty:hidden">
					{#each shoppingCart.data.below_cta_content as blok}
						<Blok content={blok} />
					{/each}
				</div>
			{/if}
		</div>
	{/if}
</section>

<style>
	.floating-background {
		background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 100%);
	}
</style>
